// Canonical questionnaire vocabulary, shared by the wizard (src) and the
// server-side validators (functions). The estimate rules engine (Phase 3) keys
// off these exact ids, so keep them stable.
import type {
  CollisionType,
  ConditionAnswer,
  CoverageAnswer,
  FaultAnswer,
  MileageBand,
  OwnershipAnswer,
} from "./types";

export interface Option<T extends string = string> {
  id: T;
  label: string;
  hint?: string;
}

export const COLLISION_TYPES: Option<CollisionType>[] = [
  { id: "rear-impact", label: "Rear impact", hint: "Something hit me from behind" },
  { id: "front-impact", label: "Front impact", hint: "I hit something in front" },
  { id: "side-impact-driver", label: "Driver-side impact", hint: "Hit on the driver's side" },
  { id: "side-impact-passenger", label: "Passenger-side impact", hint: "Hit on the passenger side" },
  { id: "sideswipe", label: "Sideswipe", hint: "Scraped along the side" },
  { id: "rollover", label: "Rollover", hint: "The vehicle rolled or tipped" },
  { id: "multi-point", label: "Multiple impacts", hint: "Hit in more than one place" },
  { id: "unknown", label: "Not sure", hint: "I'm not certain what happened" },
];

export type ImpactPoint =
  | "front"
  | "rear"
  | "driver-side"
  | "passenger-side"
  | "roof"
  | "undercarriage";

export const IMPACT_POINTS: Option<ImpactPoint>[] = [
  { id: "front", label: "Front" },
  { id: "rear", label: "Rear" },
  { id: "driver-side", label: "Driver side" },
  { id: "passenger-side", label: "Passenger side" },
  { id: "roof", label: "Roof / top" },
  { id: "undercarriage", label: "Underneath" },
];

export type Symptom =
  | "wont-start"
  | "leaking-fluid"
  | "strange-noises"
  | "pulls-to-side"
  | "warning-lights"
  | "door-wont-close"
  | "trunk-hood-wont-close"
  | "airbag-deployed"
  | "smoke-or-burning"
  | "vibration"
  | "none";

export const SYMPTOMS: Option<Symptom>[] = [
  { id: "wont-start", label: "Won't start / won't run" },
  { id: "leaking-fluid", label: "Leaking fluid" },
  { id: "strange-noises", label: "Strange noises" },
  { id: "pulls-to-side", label: "Pulls to one side when driving" },
  { id: "warning-lights", label: "Dashboard warning lights" },
  { id: "door-wont-close", label: "A door won't open or close" },
  { id: "trunk-hood-wont-close", label: "Trunk or hood won't close" },
  { id: "airbag-deployed", label: "Airbag(s) deployed" },
  { id: "smoke-or-burning", label: "Smoke or burning smell" },
  { id: "vibration", label: "Shaking or vibration" },
  { id: "none", label: "None that I've noticed" },
];

// Server-side validation helpers — reject anything not in the vocabulary.
const collisionIds = new Set(COLLISION_TYPES.map((o) => o.id));
const impactIds = new Set(IMPACT_POINTS.map((o) => o.id));
const symptomIds = new Set(SYMPTOMS.map((o) => o.id));

export const isCollisionType = (v: unknown): v is CollisionType =>
  typeof v === "string" && collisionIds.has(v as CollisionType);
export const isImpactPoint = (v: unknown): v is ImpactPoint =>
  typeof v === "string" && impactIds.has(v as ImpactPoint);
export const isSymptom = (v: unknown): v is Symptom =>
  typeof v === "string" && symptomIds.has(v as Symptom);

// --- Collision type derivation -------------------------------------------
//
// The wizard asks ONE damage question — the impact diagram — and derives the
// collision type from it. Nearly every collision type maps 1:1 onto an impact
// point, and the byCollision parts for those types already cover byImpact at
// equal-or-higher severity, so asking both questions cost the user a click that
// moved zero dollars. Only two distinctions can't be read off the diagram, and
// both are worth real money, so each gets a one-tap follow-up that appears only
// when it's load-bearing:
//   - a side selection is a cheap scrape (sideswipe: severities 2-3, 0.25
//     hidden-damage prior) or a solid hit (side-impact: 4-6, 0.60);
//   - a roof selection may be a rollover, which also loads the pillars,
//     restraints and glass, and always warrants a tow.
export type SideKind = "impact" | "sideswipe";
export type RollKind = "rollover" | "roof-hit";

export const SIDE_KINDS: Option<SideKind>[] = [
  { id: "impact", label: "A solid hit", hint: "Something struck the side, leaving a dent, not just paint" },
  { id: "sideswipe", label: "A scrape along the side", hint: "Scraped or brushed past, mostly surface damage" },
];

export const ROLL_KINDS: Option<RollKind>[] = [
  { id: "rollover", label: "Yes, it rolled or tipped", hint: "The vehicle went over onto its side or roof" },
  { id: "roof-hit", label: "No, something hit the roof", hint: "It stayed upright" },
];

const SIDE_POINTS: ImpactPoint[] = ["driver-side", "passenger-side"];
// The four points that name an *end* of the car, and so imply a collision type.
// Roof and undercarriage never do: they're additive, and undercarriage has no
// collision type at all (the only way to surface frame rails, oil pan, exhaust
// and suspension).
const END_POINTS: ImpactPoint[] = ["front", "rear", ...SIDE_POINTS];

const endsOf = (points: readonly string[]) => END_POINTS.filter((p) => points.includes(p));

// Ask "scrape or solid hit?" only when the answer changes the derived type:
// once a second end is involved the crash is multi-point either way.
export function needsSideFollowUp(points: readonly string[], roll: string): boolean {
  if (roll === "rollover") return false;
  const ends = endsOf(points);
  return ends.length > 0 && ends.every((p) => SIDE_POINTS.includes(p));
}

export const needsRollFollowUp = (points: readonly string[]): boolean => points.includes("roof");

export function deriveCollisionType(
  points: readonly string[],
  side: string,
  roll: string,
): CollisionType {
  // A rollover loads the whole safety cage; it outranks whatever else was hit.
  if (roll === "rollover") return "rollover";
  const ends = endsOf(points);
  if (ends.length === 0) return "unknown"; // roof-only, undercarriage-only, or nothing
  if (side === "sideswipe" && ends.every((p) => SIDE_POINTS.includes(p))) return "sideswipe";
  if (ends.length > 1) return "multi-point";
  switch (ends[0]) {
    case "front": return "front-impact";
    case "rear": return "rear-impact";
    case "driver-side": return "side-impact-driver";
    default: return "side-impact-passenger";
  }
}

// Reverse of deriveCollisionType, for rehydrating a resumed session: the
// follow-up answers aren't stored separately, they're folded into the type.
export function followUpsFrom(
  collisionType: string,
  points: readonly string[],
): { sideKind: SideKind | ""; rollKind: RollKind | "" } {
  return {
    sideKind:
      collisionType === "sideswipe"
        ? "sideswipe"
        : collisionType.startsWith("side-impact")
          ? "impact"
          : "",
    rollKind:
      collisionType === "rollover"
        ? "rollover"
        : collisionType && points.includes("roof")
          ? "roof-hit"
          : "",
  };
}

// --- Claim-or-cash vocabulary ---------------------------------------------
//
// Asked only on the results page, and only if the user taps the card open — the
// estimate is the input to this decision, so it can't be asked earlier, and
// most people never want it. Ordered so the branches short-circuit: an
// "other driver" answer settles it in one tap, "liability only" in two, and the
// full four fire only for at-fault-with-collision-coverage, which is exactly
// the case where the arithmetic is genuinely close.

export const FAULT_ANSWERS: Option<FaultAnswer>[] = [
  { id: "other-driver", label: "The other driver", hint: "They hit me, or they were cited" },
  { id: "me", label: "Me, or mostly me", hint: "I hit them, or fault is shared" },
  {
    id: "single-vehicle",
    label: "No other driver, I hit something",
    hint: "A pole, curb, tree, wall or guardrail",
  },
  {
    id: "not-collision",
    label: "An animal, weather, or something falling",
    hint: "Deer, hail or a branch, with nobody driving into anything",
  },
  { id: "unsure", label: "Not sure yet", hint: "Fault hasn't been settled" },
];

export const COVERAGE_ANSWERS: Option<CoverageAnswer>[] = [
  {
    id: "full",
    label: "Full coverage",
    hint: "Includes collision and comprehensive, so it repairs my own car",
  },
  {
    id: "liability-only",
    label: "Liability only",
    hint: "Covers damage I cause to others, not my own vehicle",
  },
  { id: "unsure", label: "I'm not sure", hint: "We'll assume full coverage and flag it" },
];

// Dollar tiers, plus a null "don't know" that falls back to the ruleset's
// assumed deductible rather than blocking the math.
export const DEDUCTIBLE_ANSWERS: Option<string>[] = [
  { id: "250", label: "$250" },
  { id: "500", label: "$500", hint: "The most common collision deductible" },
  { id: "1000", label: "$1,000" },
  { id: "2000", label: "$2,000 or more" },
  { id: "unknown", label: "I don't know", hint: "We'll assume $500 and tell you we did" },
];

export const OWNERSHIP_ANSWERS: Option<OwnershipAnswer>[] = [
  { id: "owned", label: "Yes, it's paid off", hint: "No lender involved" },
  { id: "financed", label: "No, I'm still making payments" },
  { id: "leased", label: "It's a lease" },
];

const faultIds = new Set(FAULT_ANSWERS.map((o) => o.id));
const coverageIds = new Set(COVERAGE_ANSWERS.map((o) => o.id));
const ownershipIds = new Set(OWNERSHIP_ANSWERS.map((o) => o.id));

export const isFaultAnswer = (v: unknown): v is FaultAnswer =>
  typeof v === "string" && faultIds.has(v as FaultAnswer);
export const isCoverageAnswer = (v: unknown): v is CoverageAnswer =>
  typeof v === "string" && coverageIds.has(v as CoverageAnswer);
export const isOwnershipAnswer = (v: unknown): v is OwnershipAnswer =>
  typeof v === "string" && ownershipIds.has(v as OwnershipAnswer);

// --- Total-loss vocabulary ------------------------------------------------
//
// Also results-page-only, and shorter than the claim flow by design: someone
// who knows what their car is worth answers ONE question and is done. Only
// "I'm not sure" opens the two follow-ups that feed the depreciation fallback,
// which is the path that needs them.

// Buckets rather than a free-text dollar box, because a bracket is what people
// actually know and a typed figure invites a precision nobody has. Bounds are
// the bracket itself, so the value range is the user's own uncertainty rather
// than one we invented. The open-topped bracket gets a finite ceiling because
// the ratio arithmetic needs one — generous enough that it can only make a
// total loss look less likely, never more.
export interface ValueBucket extends Option<string> {
  low: number;
  high: number;
}

export const VALUE_BUCKETS: ValueBucket[] = [
  { id: "0-3000", label: "Under $3,000", low: 800, high: 3000 },
  { id: "3000-6000", label: "$3,000 – $6,000", low: 3000, high: 6000 },
  { id: "6000-10000", label: "$6,000 – $10,000", low: 6000, high: 10000 },
  { id: "10000-15000", label: "$10,000 – $15,000", low: 10000, high: 15000 },
  { id: "15000-25000", label: "$15,000 – $25,000", low: 15000, high: 25000 },
  { id: "25000-plus", label: "$25,000 or more", low: 25000, high: 45000 },
];

// The escape hatch, kept out of VALUE_BUCKETS so the bucket list stays a clean
// set of ranges the engine can read bounds off.
export const VALUE_UNKNOWN_ID = "unknown";

export const MILEAGE_BANDS: Option<MileageBand>[] = [
  { id: "low", label: "Under 60,000", hint: "Low for its age, which helps the value" },
  { id: "average", label: "60,000 – 120,000", hint: "About average" },
  { id: "high", label: "120,000 – 200,000" },
  { id: "very-high", label: "Over 200,000" },
  { id: "unknown", label: "I'm not sure", hint: "We'll assume average and widen the range" },
];

export const CONDITION_ANSWERS: Option<ConditionAnswer>[] = [
  { id: "clean", label: "Clean", hint: "Well maintained, no existing damage" },
  { id: "average", label: "Average", hint: "Normal wear, maybe a scuff or two" },
  { id: "rough", label: "Rough", hint: "Existing damage, or mechanical problems already" },
];

const mileageIds = new Set(MILEAGE_BANDS.map((o) => o.id));
const conditionIds = new Set(CONDITION_ANSWERS.map((o) => o.id));

export const isMileageBand = (v: unknown): v is MileageBand =>
  typeof v === "string" && mileageIds.has(v as MileageBand);
export const isConditionAnswer = (v: unknown): v is ConditionAnswer =>
  typeof v === "string" && conditionIds.has(v as ConditionAnswer);
